import { PAYER_ACTION, CREDIT_TOPUP_MIN } from '../../../../utils/constants';

/**
 * How the program builder presents a credit shortfall.
 *
 * The API names who pays for a build and what the builder can do about a shortfall
 * (`payer`, on the chat's cost and shortfall cards and on build errors). Only the
 * workspace owner can buy: a partner owner buys DQ Tokens, a direct school or
 * network owner buys credits for its organisation, a legacy workspace owner tops up
 * credits by card. Everyone else is told whom to ask.
 *
 * `info` is the card's data: { payer, shortfall, dqt: { shortfall }, unavailable }.
 * A card saved before `payer` existed has none: it offers only Try again, and the
 * server's answer carries the payer.
 */

const BUY_ACTIONS = [PAYER_ACTION.BUY_CREDITS, PAYER_ACTION.BUY_DIRECT_CREDITS, PAYER_ACTION.BUY_DQT];

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

/** Whether this builder may buy what the build needs. */
export const canBuy = (payer) => !!payer && BUY_ACTIONS.includes(payer.action);

/** Whether the build is paid in DQ Tokens. */
export const paysInDqt = (info) => info?.payer?.currency === 'dqt' || (!info?.payer && !!info?.dqt);

/** The Top Up button's label. */
export const topUpLabel = (payer) => (payer?.action === PAYER_ACTION.BUY_DQT ? 'Top Up DQ Tokens' : 'Top Up Credits');

/** "You need 50 more credits." — or '' when the amount is not known. */
export const shortfallLine = (info) => {
    if (!info) return '';
    if (paysInDqt(info)) {
        const n = info.dqt?.shortfall;
        return n > 0 ? `You need ${fmt(n)} more DQ Tokens.` : '';
    }
    const n = info.shortfall;
    return n > 0 ? `You need ${fmt(n)} more credit${Number(n) === 1 ? '' : 's'}.` : '';
};

/** What a builder who cannot buy should do. */
export const askLine = (info) => {
    const payer = info?.payer;
    if (payer?.action === PAYER_ACTION.ASK_PARTNER) {
        return `Ask your partner to assign more credits to ${payer.org_name || 'your organisation'}, then press Try again.`;
    }
    return paysInDqt(info)
        ? 'Ask your workspace owner to buy more DQ Tokens, then press Try again.'
        : 'Ask your workspace owner to top up credits, then press Try again.';
};

/** The card top-up to start from: the shortfall rounded up, never below the minimum. */
export const suggestedCreditTopUp = (shortfall) => {
    const need = Math.ceil(Number(shortfall) || 0);
    return need > 0 ? Math.max(CREDIT_TOPUP_MIN, need) : 50;
};

/**
 * What the DQ Token / credit checkout should pre-fill for this shortfall, or null.
 * A partner's shortfall is in DQ Tokens; a direct workspace's in credits.
 */
export const checkoutNeed = (info) => {
    if (paysInDqt(info)) return info?.dqt?.shortfall > 0 ? { dqt: info.dqt.shortfall } : null;
    return info?.shortfall > 0 ? { credits: info.shortfall } : null;
};
